import React, { useState, useEffect } from "react";
import PopUpGetAward from "../../components/PopUpGetAward/PopUpGetAward";
import PopUpAddress from "../../components/PopUpAddress/PopUpAddress";
import { ReactComponent as Coin } from "./../../../assets/svg/coin.svg";
import { useTranslation } from "react-i18next";
import "./Store.scss";

import { useAuthValidation } from "../../../hooks/useAuthValidation";
import useStoreItems from "../../../hooks/useStoreItems";
import DefaultLayout from "../../components/Layouts/DefaultLayout";

function Store() {
  const { t } = useTranslation();
  const { user, loading } = useAuthValidation();
  const { items, error } = useStoreItems(user, loading);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItemValue, setSelectedItemValue] = useState(null);
  const [selectedItemTitle, setSelectedItemTitle] = useState(null);

  const userCoins = user?.rewards?.coins || 0;
  const address = user?.address?.address || "";

  const openModal = (id, title, value) => {
    setIsModalOpen(true);
    setSelectedItemId(id);
    setSelectedItemTitle(title);
    setSelectedItemValue(value);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItemId(null);
    setSelectedItemTitle(null);
    setSelectedItemValue(null);
  };

  if (error) {
    return <div>{t("error-fetching-items")}</div>;
  }

  if (loading) {
    return <div className="loading">{t("loading")}</div>;
  }

  if (!user) {
    return null;
  }

  const normalizedItems = items.map((item) => ({
    ...item,
    value: Number(item.value),
  }));

  const normalizedUserCountry = String(user?.address?.country ?? "")
    .trim()
    .toLowerCase();

  const filteredByCountry = normalizedUserCountry
    ? normalizedItems.filter((item) => {
        const itemCountries = Array.isArray(item.countries)
          ? item.countries
          : String(item.countries ?? "")
              .split(",")
              .map((country) => country.trim())
              .filter(Boolean);

        return itemCountries.some(
          (country) => country.toLowerCase() === normalizedUserCountry
        );
      })
    : normalizedItems;

  const availableItems = filteredByCountry.filter(
    (item) => Number(userCoins) >= item.value
  );
  const unavailableItems = filteredByCountry.filter(
    (item) => Number(userCoins) < item.value
  );

  return (
    <DefaultLayout>
      <section className="awards">
        <div className="container">
          <div className="header-content">
            <div className="column">
              <h2>{t("award-title")}</h2>
              <p>{t("award-text")}</p>
            </div>
            <div className="row">
              <span>Coins</span>
              <Coin />
              <span className="coin">{userCoins}</span>
            </div>
          </div>

          <ul className="available-awards">
            {availableItems.map((item, index) => (
              <li key={index}>
                <div className="image-content" style={{ backgroundImage: `url(${item.image.link})` }}>
                  <div className="coins row">
                    <Coin />
                    <span>{item.value}</span>
                  </div>
                </div>
                <div className="information-content">
                  <p>{item.title}</p>
                  <button onClick={() => openModal(item.id, item.title, item.value)}>{t("award-available")}</button>
                </div>
              </li>
            ))}
          </ul>

          {unavailableItems.length > 0 && (
            <div className="unavailable-awards">
              <span>{t("award-more")}</span>
              <ul>
                {unavailableItems.map((item, index) => {
                  const remainingCoins = item.value - userCoins;
                  return (
                    <li key={index}>
                      <div className="image-content" style={{ backgroundImage: `url(${item.image.link})` }}>
                        <div className="coins row">
                          <Coin />
                          <span>{item.value}</span>
                        </div>
                      </div>
                      <div className="information-content">
                        <p>{item.title}</p>
                        <button>
                          <span>
                            {t("award-missing")} <Coin /> {remainingCoins}
                          </span>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </section>

      <PopUpGetAward
        isOpen={isModalOpen}
        closeModal={closeModal}
        itemId={selectedItemId}
        itemTitle={selectedItemTitle}
        itemValue={selectedItemValue}
      />
    </DefaultLayout>
  );
}

export default Store;
